import { useQuery } from '@tanstack/react-query';
import { getDashboardData } from '../../services/dashboardService';
import { DashboardPaging } from '../../interfaces/DashboardPaging';
import { dashboardQueryKey, keepWhileSameDataSet } from './dashboardQueryKey';

interface UseDashboardDataParams {
  companyId: string | undefined;
  userId?: string;
  startDate: string | undefined;
  endDate: string | undefined;
  agentIds: string | undefined;
  enabled?: boolean;
  // Omit to get every call in the range, as before.
  paging?: DashboardPaging;
  // Selected category chips, empty means all.
  categories?: string[];
}

export const useDashboardData = ({
  companyId,
  userId,
  startDate,
  endDate,
  agentIds,
  enabled = true,
  paging,
  categories = [],
}: UseDashboardDataParams) => {
  const queryKey = dashboardQueryKey(
    'dashboard-data',
    { companyId, userId, startDate, endDate, agentIds },
    paging,
    categories
  );
  return useQuery<any, Error>({
    queryKey,
    queryFn: async () => {
      if (!companyId) {
        return null;
      }
      return await getDashboardData(
        companyId,
        startDate ?? '',
        endDate ?? '',
        agentIds,
        userId,
        paging,
        categories
      );
    },
    // No retries, a timeout retried three times only triples the load
    retry: false,
    enabled: enabled && !!companyId && !!agentIds,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    placeholderData: keepWhileSameDataSet(queryKey),
    refetchOnMount: false, // Don't refetch when navigating between pages
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch when reconnecting
  });
};
